// An analysis table says which entities it is about through an entityBinding. That binding names
// either a Builder layer the run used, or - for the many stored results no Builder run points at any
// more - the run's own input file in Cloud Storage. Both are drawn the same way, so the map treats a
// result-backed network as a layer with a reserved id and loads its rows from the result's prefix.

export const RESULT_LAYER_PREFIX = "result:";
export const BINDABLE_ENTITY_TYPES = ["links", "nodes", "parcels"];

const RESULT_LAYER_FILENAMES = {
  links: "berkeley_links.csv",
  nodes: "berkeley_nodes.csv",
  parcels: "berkeley_parcels.csv",
};

/**
 * The layer id a binding selects, whether it names a Builder layer or a stored result. A result has
 * a separate network per entity type, so the type belongs in the id: without it, a links table and a
 * parcels table from the same run would claim one layer and the second would take the first's file.
 */
export function bindingLayerKey(binding) {
  const layerId = String(binding?.layerId || "").trim();
  if (layerId) return layerId;
  const resultId = String(binding?.resultId || "").trim();
  const type = String(binding?.type || "").trim();
  if (!resultId || !BINDABLE_ENTITY_TYPES.includes(type)) return "";
  return `${RESULT_LAYER_PREFIX}${type}:${resultId}`;
}

export function isResultLayerId(layerId) {
  return String(layerId || "").startsWith(RESULT_LAYER_PREFIX);
}

/** True when a binding identifies entities the map can actually draw. */
export function isDrawableBinding(binding) {
  return (
    BINDABLE_ENTITY_TYPES.includes(String(binding?.type || "")) &&
    !!bindingLayerKey(binding) &&
    !!String(binding?.idKey || "").trim()
  );
}

/**
 * A layer record standing for the network a run actually used, so the existing layer loading,
 * rendering and selection paths work unchanged. It is kept out of the Builder's layer lists: it is
 * not an authored layer and must not be offered for editing, deletion or as a run input.
 */
export function resultLayerDoc(binding, label = "") {
  const resultId = String(binding?.resultId || "").trim();
  const type = String(binding?.type || "");
  if (!resultId || !BINDABLE_ENTITY_TYPES.includes(type)) return null;
  const filename = String(binding?.fileName || "").trim() || RESULT_LAYER_FILENAMES[type];
  if (!filename) return null;
  return {
    id: bindingLayerKey(binding),
    type,
    filename,
    resultId,
    isResultLayer: true,
    name: label ? `${label} (run network)` : "Run network",
  };
}
